import {
  Granularity,
  RollingWindowConfigUnit,
  TimeRangeConfig,
  TimeRangeKind,
  type ProjectChartDatapoint,
} from '@/api/generated/model';
import { getChartNonEmptyBucketSpan } from '@/domains/pages/shared/trim-chart-empty-pan';
import { getFixedRangeTrendQuery, type TrendQueryConfig } from '@/domains/pages/shared/trend-query';
import { formatToLocaleDateValue, getTimezoneName } from '@/utils/date.util';
import dayjs from 'dayjs';

export const AI_PROFICIENCY_ROLLING_WINDOW = {
  size: 30,
  unit: RollingWindowConfigUnit.day,
};

export const AI_PROFICIENCY_MONTHLY_MIN_BUCKETS = 6;

export function getAiProficiencyRollingTrendQuery(
  granularity: typeof Granularity.month | typeof Granularity.week,
  now: Date = new Date(),
  effectiveNow?: Date,
): TrendQueryConfig {
  return getFixedRangeTrendQuery(TimeRangeKind['12_months'], granularity, now, effectiveNow);
}

export function getAiProficiencyMonthlyRollingTrendQuery(
  now: Date = new Date(),
  effectiveNow?: Date,
): TrendQueryConfig {
  return getAiProficiencyRollingTrendQuery(Granularity.month, now, effectiveNow);
}

function resolveWeeklyFallbackTimeRange(
  monthlyOrgChart: ProjectChartDatapoint | undefined,
  monthlyTrendQuery: TrendQueryConfig,
): TimeRangeConfig {
  const span = getChartNonEmptyBucketSpan(monthlyOrgChart);
  if (!span) {
    return monthlyTrendQuery.time_range;
  }

  const buckets = monthlyOrgChart?.x_label_values ?? [];
  const queryEnd = dayjs(monthlyTrendQuery.time_range.end);
  const dataStart = dayjs(buckets[span.startIndex]).startOf('month');
  const dataEnd = dayjs(buckets[span.endIndex]).endOf('month');
  const paddedStart = dataStart.subtract(AI_PROFICIENCY_ROLLING_WINDOW.size, 'day');
  const boundedEnd = dataEnd.isAfter(queryEnd) ? queryEnd : dataEnd;

  return {
    kind: TimeRangeKind.custom,
    start: formatToLocaleDateValue(paddedStart.valueOf()),
    end: formatToLocaleDateValue(boundedEnd.valueOf()),
    timezone: monthlyTrendQuery.time_range.timezone ?? getTimezoneName(),
    recompute: false,
  };
}

/** Weekly retry scoped to the monthly chart's non-empty bucket span. */
export function buildAiProficiencyWeeklyFallbackTrendQuery(
  monthlyOrgChart: ProjectChartDatapoint | undefined,
  monthlyTrendQuery: TrendQueryConfig,
): TrendQueryConfig {
  return {
    time_range: resolveWeeklyFallbackTimeRange(monthlyOrgChart, monthlyTrendQuery),
    granularity: Granularity.week,
    bucketKind: monthlyTrendQuery.bucketKind,
  };
}
